import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import GridViewIcon from '@mui/icons-material/GridView';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HistoryIcon from '@mui/icons-material/History';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';

import { useDispatch, useSelector } from 'react-redux';
import { COLOR_GREY_400, COLOR_GREY_600, COLOR_PRIMARY_2 } from 'src/colors';
import TabBar from '../Components/Tabs/TabBar';
import { COLOR_PRIMARY_DARK, LIGHT_GREY } from 'src/const';
import { callApi } from 'src/core/callApi';
import {
  getRedirectionLink,
  getVisitedMarketsFromLocalStorage,
  handleSaveDeleteAdvancedMarket,
  sortUserMarkets,
} from 'src/utils/functions';
import { getUserInfo } from 'src/store/Dashboard/actions';
import SortTypeSelectionMenu from '../HOC/SortTypeSelectionMenu';
import ShareLinkModal from '../Components/Modals/ShareLinkModal';
import TooltipNew from '../HOC/Tooltips/TooltipNew';
import AdvancedDashboard from './Advanced/AdvancedDashboard';
import { logAmplitudeEvent } from 'src/utils/analytics';
import FilterChip from '../Components/Chips/FilterChip';
import { SubscriptionModalOrigin, SubscriptionPlan } from 'src/interfaces/idashboard';

const CardOptionMenuButton = ({ currentCardId, handleOptionClick }) => {
  return (
    <Button
      onClick={handleOptionClick}
      id={currentCardId}
      size="small"
      sx={{
        position: 'absolute',
        top: '4px',
        right: '4px',
        background: '#fff',
        color: COLOR_PRIMARY_DARK,
        width: '20px',
        maxWidth: '20px',
        minWidth: '20px',
        zIndex: 1000,
      }}
    >
      <MoreVertIcon />
    </Button>
  );
};

const MarketDeleteDialog = ({
  isDeleteDialogOpen,
  setIsDeleteDialogOpen,
  handleDeleteReport,
}: {
  isDeleteDialogOpen: boolean;
  setIsDeleteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleDeleteReport: () => void;
}) => {
  return (
    <Dialog open={isDeleteDialogOpen}>
      <DialogTitle id="alert-dialog-title">
        Are you sure you want to delete this market?
      </DialogTitle>
      <DialogActions sx={{ padding: '20px' }}>
        <Button
          variant="outlined"
          color="info"
          startIcon={<CloseRoundedIcon />}
          onClick={() => setIsDeleteDialogOpen(false)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteOutlineRoundedIcon />}
          onClick={() => {
            handleDeleteReport();
            setIsDeleteDialogOpen(false);
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MenuShareDeleteMarketCard = ({
  urlPath,
  currentReportCardId,
  isOptionsOpen,
  setIsOptionsOpen,
  setComingSoonModalOpen,
  handleDeleteReport,
  handleSave,
  isApliCalling,
  marketType,
  userSubscriptionPlan,
}) => {
  const menuItemStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
  };

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isShareLinkModalOpen, setIsShareLinkModalOpen] = useState<boolean>(false);

  return (
    <>
      <Menu
        id="market-menu"
        anchorEl={document.getElementById(currentReportCardId)}
        open={isOptionsOpen}
        onClose={() => setIsOptionsOpen(false)}
      >
        <MenuItem onClick={() => setIsShareLinkModalOpen(true)}>
          <Box sx={menuItemStyle}>
            <ReplyRoundedIcon sx={{ transform: 'scaleX(-1)' }} />
            Share
          </Box>
        </MenuItem>

        {marketType !== 'saved' && (
          <MenuItem onClick={handleSave}>
            <TooltipNew
              title={
                userSubscriptionPlan === 'scale'
                  ? 'Save advanced details'
                  : 'Only available on scale plan'
              }
              content={''}
            >
              <Box sx={menuItemStyle}>
                {isApliCalling === 'save' ? (
                  <CircularProgress size={20} />
                ) : (
                  <BookmarkAddOutlinedIcon />
                )}{' '}
                Save
              </Box>
            </TooltipNew>
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            setIsDeleteDialogOpen(true);
          }}
        >
          <Box sx={menuItemStyle}>
            {isApliCalling === 'delete' ? (
              <CircularProgress size={20} />
            ) : (
              <DeleteOutlineRoundedIcon />
            )}{' '}
            Delete
          </Box>
        </MenuItem>
      </Menu>

      <MarketDeleteDialog
        isDeleteDialogOpen={isDeleteDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
        handleDeleteReport={handleDeleteReport}
      />

      <ShareLinkModal
        open={isShareLinkModalOpen}
        setOpen={setIsShareLinkModalOpen}
        shareUrl={`${window.location.origin}/${urlPath}`}
      />
    </>
  );
};

const MenuShareDeleteSubMarketCard = ({
  urlPath,
  currentCardId,
  isOptionsOpen,
  setIsOptionsOpen,
  handleDeleteReport,
  isApliCalling,
}) => {
  const menuItemStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
  };

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isShareLinkModalOpen, setIsShareLinkModalOpen] = useState<boolean>(false);

  return (
    <>
      <Menu
        id="market-menu"
        anchorEl={document.getElementById(currentCardId)}
        open={isOptionsOpen}
        onClose={() => setIsOptionsOpen(false)}
      >
        <MenuItem onClick={() => setIsShareLinkModalOpen(true)}>
          <Box sx={menuItemStyle}>
            <ReplyRoundedIcon sx={{ transform: 'scaleX(-1)' }} />
            Share
          </Box>
        </MenuItem>

        <MenuItem
          onClick={() => {
            setIsDeleteDialogOpen(true);
          }}
        >
          <Box sx={menuItemStyle}>
            {isApliCalling === 'delete' ? (
              <CircularProgress size={20} />
            ) : (
              <DeleteOutlineRoundedIcon />
            )}{' '}
            Delete
          </Box>
        </MenuItem>
      </Menu>

      <MarketDeleteDialog
        isDeleteDialogOpen={isDeleteDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
        handleDeleteReport={handleDeleteReport}
      />

      <ShareLinkModal
        open={isShareLinkModalOpen}
        setOpen={setIsShareLinkModalOpen}
        shareUrl={`${window.location.origin}/${urlPath}`}
      />
    </>
  );
};

const MarketCard = ({
  district,
  country,
  city,
  isSubscribed,
  isDemo,
  index,
  id,
  marketType,
  setSavedMarkets,
  user,
  searchMarketOrigin,
  recentlyVisitedMarketsFromLocalStorage,
  setRecentlySearchedMarkets,
  recentlySearchedMarkets,
}) => {
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isApliCalling, setIsApliCalling] = useState<'delete' | 'save' | null>(null);

  const currentCardId = 'market-card-' + index;

  const handleClick = () => {
    const redirectTo = getRedirectionLink(country.split(' ').join('-'), city, district);
    window.history.pushState({}, '', redirectTo);
    window.location.reload();
  };

  const handleOptionClick = event => {
    event.stopPropagation();
    setIsOptionsOpen(true);
  };

  const ifMarketInLocalStorageDelete = (
    recentlySearchedMarkets,
    recentlyVisitedMarkets,
    district,
    city,
    country,
    index
  ) => {
    const filteredMarket = recentlyVisitedMarkets.filter(market => {
      const marketDistrict = market.district?.toLowerCase() || '0';
      const marketCity = market.city?.toLowerCase() || '0';
      const marketCountry = market.country?.toLowerCase() || '0';

      const isMatch =
        marketDistrict === district.toLowerCase() &&
        marketCity === (city?.toLowerCase() || '0') &&
        marketCountry === (country?.toLowerCase() || '0');

      return !isMatch;
    });
    if (recentlyVisitedMarkets.length !== filteredMarket.length) {
      localStorage.setItem('visited-markets', JSON.stringify(filteredMarket));
      recentlySearchedMarkets.splice(index, 1);
    }
    return recentlySearchedMarkets;
  };

  const ifMarketInDynamoDbDelete = async (
    recentlySearchedMarkets,
    district,
    city,
    country,
    index
  ) => {
    try {
      const URL =
        'https://bi0u0j079i.execute-api.us-east-2.amazonaws.com/default/deleteSavedSearchMarket';
      const response = await callApi(
        URL,
        { queryParams: { district: district, city: city, country: country } },
        true
      );

      if (response?.message === 'success') {
        recentlySearchedMarkets.splice(index, 1);
      }
    } catch (err) {
      console.log('Error while deleting the saved market from dynamodb: ', err);
    }

    return recentlySearchedMarkets;
  };

  const handleDeleteReport = async () => {
    setIsApliCalling('delete');
    if (marketType === 'saved') {
      const response = await handleSaveDeleteAdvancedMarket(id, '', '', '', '', '', 'delete');
      if (response) {
        setSavedMarkets(prevSavedMarkets =>
          [...prevSavedMarkets].filter(market => market.id !== id)
        );
      }
      setIsApliCalling(null);
      setIsOptionsOpen(false);
      return;
    }

    let filteredMarkets =
      searchMarketOrigin === 'localstorage'
        ? ifMarketInLocalStorageDelete(
            [...recentlySearchedMarkets],
            recentlyVisitedMarketsFromLocalStorage,
            district,
            city,
            country,
            index
          )
        : recentlySearchedMarkets;

    filteredMarkets =
      searchMarketOrigin === 'dynamodb'
        ? await ifMarketInDynamoDbDelete([...filteredMarkets], district, city, country, index)
        : filteredMarkets;

    setRecentlySearchedMarkets(filteredMarkets);
    setIsApliCalling(null);
    setIsOptionsOpen(false);
  };

  const handleSave = async () => {
    setIsApliCalling('save');
    const isSaved = await handleSaveDeleteAdvancedMarket(
      id,
      district,
      city,
      null,
      country.split(' ').join('-'),
      user.subscriptionPlan,
      'save'
    );
    if (isSaved) {
      setSavedMarkets(prev => [
        ...prev,
        {
          id: id,
          district: district,
          country: '0',
          city: city,
          isSubscribed: true,
        },
      ]);
    }
    setIsOptionsOpen(false);
    setIsApliCalling(null);
  };

  if (!district) {
    return <></>;
  }

  return (
    <>
      <Card
        sx={{
          position: 'relative',
          display: 'flex',
          minWidth: '300px',
          maxWidth: '400px',
          width: '100%',
          cursor: 'pointer',
        }}
        onClick={handleClick}
      >
        <CardMedia
          component="img"
          sx={{ width: '35%' }}
          image="https://airbtics-wordpress-market-assets.s3.us-east-2.amazonaws.com/map-image-no-label-demo.png"
        />
        <CardOptionMenuButton currentCardId={currentCardId} handleOptionClick={handleOptionClick} />

        <Chip
          label={isDemo ? 'Demo Market' : 'Market'}
          sx={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            background: '#fff',
            border: '1px solid ' + COLOR_PRIMARY_2,
            color: COLOR_PRIMARY_2,
            fontWeight: 'bold',
            textTransform: 'capitalize',
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <CardContent>
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
            >
              {district.slice(0, 12)} {district.length > 12 && '...'}
            </Typography>
          </CardContent>
          <CardContent sx={{ paddingBottom: '10 !important' }}>
            <Typography
              variant="subtitle1"
              sx={{ alignSelf: 'flex-end', fontSize: '14px', textTransform: 'capitalize' }}
            >
              {country != '0' && country.slice(0, 15)}
              {country != '0' && country.length > 15 && '...'}
              {country == '0' && <>&nbsp;</>}
            </Typography>
          </CardContent>
        </Box>
      </Card>

      <MenuShareDeleteMarketCard
        urlPath={`airbnb-data/${country
          .split(' ')
          .join('-')}/${city}/${district}`.toLocaleLowerCase()}
        currentReportCardId={currentCardId}
        isOptionsOpen={isOptionsOpen}
        setIsOptionsOpen={setIsOptionsOpen}
        setComingSoonModalOpen={() => {}}
        handleDeleteReport={handleDeleteReport}
        handleSave={handleSave}
        isApliCalling={isApliCalling}
        marketType={marketType}
        userSubscriptionPlan={user.subscriptionPlan}
      />
    </>
  );
};

const SubmarketCard = ({
  district,
  parentName,
  id,
  index,
  savedSubmarkets,
  setSavedSubmarkets,
  userSubscriptionPlan,
}) => {
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isApliCalling, setIsApliCalling] = useState<'delete' | null>(null);

  const currentCardId = 'sub-market-card-' + index;

  const handleClick = () => {
    const redirectTo = `${window.location.origin}/airbnb-data/submarket/${id}`;
    window.history.pushState({}, '', redirectTo);
    window.location.reload();
  };

  const handleOptionClick = event => {
    event.stopPropagation();
    setIsOptionsOpen(true);
  };

  const handleDeleteSubMarket = async savedSubmarkets => {
    setIsApliCalling('delete');
    try {
      const URL = 'https://3jg8y4cgz1.execute-api.us-east-2.amazonaws.com/default/handleSubmarket';
      const response = await callApi(URL, { queryParams: { id: id } }, true, false, '', 'DELETE');

      if (response?.message === 'success') {
        savedSubmarkets.splice(index, 1);
        setSavedSubmarkets(savedSubmarkets);
      }
    } catch (err) {
      console.log('Error while deleting the saved sub-markets: ', err);
    }
    setIsApliCalling(null);
  };

  return (
    <>
      <Card
        sx={{
          position: 'relative',
          display: 'flex',
          minWidth: '300px',
          maxWidth: '400px',
          width: '100%',
          cursor: 'pointer',
        }}
        onClick={handleClick}
      >
        <CardMedia
          component="img"
          sx={{ width: '35%' }}
          image="https://airbtics-wordpress-market-assets.s3.us-east-2.amazonaws.com/map-image-no-label-demo.png"
        />

        <CardOptionMenuButton currentCardId={currentCardId} handleOptionClick={handleOptionClick} />

        <Chip
          label={'Submarket'}
          sx={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            background: '#fff',
            border: '1px solid ' + COLOR_PRIMARY_2,
            color: COLOR_PRIMARY_2,
            fontWeight: 'bold',
            textTransform: 'capitalize',
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <CardContent>
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
            >
              {district.slice(0, 12)} {district.length > 12 && '...'}
            </Typography>
          </CardContent>
          <CardContent sx={{ paddingBottom: '10 !important' }}>
            <Typography
              variant="subtitle1"
              sx={{ alignSelf: 'flex-end', fontSize: '14px', textTransform: 'capitalize' }}
            >
              {parentName != '0' && parentName.slice(0, 15)}
              {parentName != '0' && parentName.length > 15 && '...'}
              {parentName == '0' && <>&nbsp;</>}
            </Typography>
          </CardContent>
        </Box>
      </Card>

      <MenuShareDeleteSubMarketCard
        urlPath={`/airbnb-data/submarket/${id}`}
        currentCardId={currentCardId}
        isOptionsOpen={isOptionsOpen}
        setIsOptionsOpen={setIsOptionsOpen}
        handleDeleteReport={() => handleDeleteSubMarket([...savedSubmarkets])}
        isApliCalling={isApliCalling}
      />
    </>
  );
};

const CustomSwitch = ({ view, setView, tab }) => {
  const theme = useTheme();

  if (tab !== 'saved') return <></>;

  return (
    <Paper
      sx={{
        padding: 1,
        borderRadius: 20,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[3],
      }}
    >
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        sx={{
          background: LIGHT_GREY,
          borderRadius: 100,
        }}
      >
        <IconButton
          sx={{
            fontSize: 10,
            backgroundColor: view == 'markets' ? COLOR_PRIMARY_2 : LIGHT_GREY,
          }}
          onClick={() => setView('markets')}
        >
          <GridViewIcon
            sx={{
              color: view == 'markets' ? 'white' : COLOR_GREY_600,
            }}
          />
        </IconButton>

        <IconButton
          sx={{
            fontSize: 10,
            backgroundColor: view == 'advancedDashboard' ? COLOR_PRIMARY_2 : LIGHT_GREY,
          }}
          onClick={() => setView('advancedDashboard')}
        >
          <TableChartIcon
            sx={{
              color: view == 'advancedDashboard' ? 'white' : COLOR_GREY_600,
            }}
          />
        </IconButton>
      </Grid>
    </Paper>
  );
};

const OverviewLandingPage = ({
  SearchBox,
  handleMarketSidebarClick,
  dummyMarket,
  handleOnSampleClick,
}: {
  SearchBox: (source: string) => JSX.Element;
  handleMarketSidebarClick: (event: string) => Promise<void>;
  dummyMarket: any;
  handleOnSampleClick: (
    reqSubscriptionPlan?: SubscriptionPlan,
    origin?: SubscriptionModalOrigin | null
  ) => void;
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const user = useSelector(state => (state as any).dashboard.user);
  const dispatch = useDispatch();

  useEffect(() => {
    logAmplitudeEvent('market_landing_page_visited');
  }, []);

  interface UserMarketCardType {
    district: string;
    country: string;
    city: string;
    isSubscribed: boolean;
    isDemo: boolean;
  }

  const [recentlySearchedMarkets, setRecentlySearchedMarkets] = useState<UserMarketCardType[]>([]);

  const [savedMarkets, setSavedMarkets] = useState<UserMarketCardType[]>([]);
  const [sortingOrder, setSortingOrder] = useState<'alpha-a-z' | 'alpha-z-a' | undefined>(
    undefined
  );

  useEffect(() => {
    dispatch(getUserInfo());
  }, [getUserInfo]);

  const [savedSubmarkets, setSavedSubmarkets] = useState<any[]>([]);

  const getSavedSubmarkets = async () => {
    const url = 'https://3jg8y4cgz1.execute-api.us-east-2.amazonaws.com/default/handleSubmarket';
    const data = await callApi(url, {}, true, false, '', 'GET', true);
    if (data.message) {
      setSavedSubmarkets(data.message);
    }
  };

  useEffect(() => {
    const subscribedMarkets =
      user.regions &&
      Array.isArray(user.regions) &&
      [...user.regions].reverse().map(market => ({
        district: market.region,
        country: market.country ? market.country?.toLowerCase() : '0',
        city: market.city ? market.city?.toLowerCase() : '0',
        isSubscribed: true,
        isDemo: market.region?.toLowerCase() === dummyMarket.district?.toLowerCase(),
        origin: 'dynamodb',
      }));
    const recentlyVisitedMarkets = getVisitedMarketsFromLocalStorage();

    const marketMap = new Map();

    recentlyVisitedMarkets &&
      Array.isArray(recentlyVisitedMarkets) &&
      recentlyVisitedMarkets.reverse().forEach(market => {
        marketMap.set(market.district?.toLowerCase(), {
          ...market,
          country: market.country ? market.country?.toLowerCase() : '0',
          city: market.city ? market.city?.toLowerCase() : '0',
          isSubscribed: false,
          isDemo: market.district?.toLowerCase() === dummyMarket.district?.toLowerCase(),
          origin: 'localstorage',
        });
      });

    subscribedMarkets.forEach(market => {
      !marketMap.has(market.district?.toLowerCase()) &&
        marketMap.set(market.district?.toLowerCase(), market);
    });

    const uniqueMarkets = Array.from(marketMap.values());
    setRecentlySearchedMarkets(uniqueMarkets);
    getSavedSubmarkets();

    const savedMarkets =
      user.savedMarkets &&
      [...user.savedMarkets].reverse().map(market => ({
        id: market.id,
        district: market.district.toLowerCase(),
        country: '0',
        city: market.city ? market.city?.toLowerCase() : '0',
        isSubscribed: true,
        isDemo: false,
      }));
    setSavedMarkets(savedMarkets);
  }, [user]);

  const overviewLandingTabs = [
    {
      label: 'Recently Searched',
      value: 'recent',
      icon: <HistoryIcon />,
      onclick: () => {
        setTab('recent');
      },
    },
    {
      label: 'Saved Markets',
      value: 'saved',
      icon: <BookmarkIcon />,
      onclick: () => {
        setTab('saved');
      },
    },
  ];

  const [tab, setTab] = useState<'recent' | 'saved' | 'submarkets'>('recent');
  const [view, setView] = useState<'markets' | 'advancedDashboard'>('markets');
  const [filter, setFilter] = useState<'markets' | 'submarkets' | 'both'>('submarkets');
  const [showAdvancedDashboard, setShowAdvancedDashboard] = useState<boolean>(false);
  const [advancedTab, setAdvancedTab] = useState<number>(0);
  const [isScaleUser, setIsScaleUser] = useState(false);

  useEffect(() => {
    setShowAdvancedDashboard(view === 'advancedDashboard' && tab === 'saved' ? true : false);
  }, [view, tab]);

  useEffect(() => {
    if (isScaleUser) {
      setTab('saved');
    }
  }, [isScaleUser]);

  const [sortedMarketsToShow, setSortedMarketsToShow] = useState<any[]>([]);
  const [sortedRecentlySearchedMarketsToShow, setSortedRecentlySearchedMarketsToShow] = useState<
    any[]
  >([]);

  useEffect(() => {
    const combinedMarkets = [
      ...(savedMarkets || []),
      ...(savedSubmarkets || []).map(submarket => ({
        ...submarket,
        isSubmarket: true,
        district: submarket.name,
      })),
    ];

    const filteredMarkets = combinedMarkets.filter(market => {
      if (filter === 'markets') return !market.isSubmarket;
      if (filter === 'submarkets') return market.isSubmarket;
      return true;
    });

    setSortedMarketsToShow(
      sortingOrder ? sortUserMarkets(filteredMarkets, 'district', sortingOrder) : filteredMarkets
    );
    setSortedRecentlySearchedMarketsToShow(
      sortingOrder
        ? sortUserMarkets(recentlySearchedMarkets, 'district', sortingOrder)
        : recentlySearchedMarkets
    );
  }, [sortingOrder, savedMarkets, savedSubmarkets, recentlySearchedMarkets, filter]);

  const recentlyVisitedMarketsFromLocalStorage = getVisitedMarketsFromLocalStorage();

  const handleFilterChipOnClick = value => {
    setFilter(value);
  };

  useEffect(() => {
    if (user.subscriptionPlan == 'scale' || user.role == 'admin') {
      setIsScaleUser(true);
      setFilter('both');
    } else {
      setIsScaleUser(false);
    }
  }, [user]);

  const disabledFilterStyle = {
    backgroundColor: '#fff',
    color: COLOR_GREY_400,
    borderColor: COLOR_GREY_400,
  };

  const filterSection = (
    <Grid container item spacing={1}>
      <Grid item>
        <FilterChip
          label="All"
          value="both"
          variant={filter === 'both' ? 'filled' : 'outlined'}
          handleOnClick={isScaleUser ? handleFilterChipOnClick : () => {}}
          tooltipText={isScaleUser ? '' : 'Only available in Scale plan'}
          styles={isScaleUser ? {} : disabledFilterStyle}
        />
      </Grid>
      <Grid item>
        <FilterChip
          label="Markets Only"
          value="markets"
          variant={filter === 'markets' ? 'filled' : 'outlined'}
          handleOnClick={isScaleUser ? handleFilterChipOnClick : () => {}}
          tooltipText={isScaleUser ? '' : 'Only available in Scale plan'}
          styles={isScaleUser ? {} : disabledFilterStyle}
        />
      </Grid>
      <Grid item>
        <FilterChip
          label="Submarkets Only"
          value="submarkets"
          variant={filter === 'submarkets' ? 'filled' : 'outlined'}
          handleOnClick={handleFilterChipOnClick}
        />
      </Grid>
    </Grid>
  );

  const sortingSection = (
    <SortTypeSelectionMenu
      optionsMap={{
        'alpha-a-z': {
          displayName: 'Location Name A-Z',
          sortKey: 'district',
        },
        'alpha-z-a': {
          displayName: 'Location Name Z-A',
          sortKey: 'district',
        },
      }}
      sortingOrder={sortingOrder}
      setStateOnChange={setSortingOrder}
    />
  );

  return (
    <Grid
      container
      item
      sx={{
        paddingBottom: matches ? '270px' : '70px',
        background: '#FFF',
        width: '100%',
      }}
      xs={12}
    >
      <Grid
        container
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px',
          height: '50%',
        }}
      >
        <Grid container item justifyContent="center" xs={12}>
          <Grid
            item
            container
            sx={{
              padding: '5px',
              height: '90px',
              maxWidth: '1600px',
              borderRadius: '12px',
              background: '#fff',
              mt: 2,
            }}
            lg={8}
            md={8}
            xs={12}
          >
            {SearchBox('landing_page')}
          </Grid>
        </Grid>
      </Grid>

      <Grid
        container
        item
        xs={12}
        sx={{
          width: '100%',
          backgroundColor: '#E7F2F4',
          minHeight: '100px',
          borderTopLeftRadius: '40px',
        }}
      >
        <Grid
          container
          item
          xs
          sx={{
            background: '#fff',
            marginTop: '20px',
            borderTopLeftRadius: '40px',
            padding: '20px',
          }}
          gap={4}
        >
          <Grid item xs={12}>
            <TabBar
              tabList={overviewLandingTabs}
              currentTab={tab}
              color={COLOR_PRIMARY_2}
              renderLabelCondition={() => true}
              actionButton={
                matches || view == 'advancedDashboard'
                  ? []
                  : [
                      <Grid item sx={{ mb: 1 }}>
                        {sortingSection}
                      </Grid>,
                    ]
              }
              showActionButton={true}
              transparentBackground={true}
            />
          </Grid>

          {tab == 'saved' && !isScaleUser && (
            <Grid xs={12}>
              <Alert severity="info">
                Saved Markets is a Scale Plan feature that lets you save markets and compare their
                summaries in Excel format.{' '}
                <Link onClick={() => handleOnSampleClick('scale')} style={{ cursor: 'pointer' }}>
                  Upgrade
                </Link>{' '}
                to the Scale Plan to unlock this feature and explore more insights in the{' '}
                <Link href="/advanced-dashboard" target="_blank">
                  Advanced Dashboard
                </Link>{' '}
                .
              </Alert>
            </Grid>
          )}

          {tab === 'saved' && matches && (
            <Grid container item alignItems="flex-start" xs={12} gap={2}>
              <Grid item xs={12} container justifyContent="space-between" alignItems="center">
                {view !== 'advancedDashboard' ? sortingSection : <Grid></Grid>}
                <CustomSwitch view={view} setView={setView} tab={tab} />
              </Grid>

              <Grid item xs={12}>
                {view !== 'advancedDashboard' && filterSection}
              </Grid>
            </Grid>
          )}

          {tab === 'saved' && !matches && (
            <Grid container item alignItems="flex-start" xs={12} justifyContent="space-between">
              <Grid item>{view !== 'advancedDashboard' && filterSection}</Grid>
              <Grid item>
                <CustomSwitch view={view} setView={setView} tab={tab} />
              </Grid>
            </Grid>
          )}

          {matches && tab !== 'saved' && sortingSection}

          {showAdvancedDashboard && (
            <Grid container item xs={12}>
              <AdvancedDashboard
                matches={matches}
                user={user}
                advancedTab={advancedTab}
                setAdvancedTab={setAdvancedTab}
                dummyMarket={dummyMarket}
                handleOnSampleClick={handleOnSampleClick}
                isLandingView={true}
              />
            </Grid>
          )}

          {tab == 'recent' && (
            <Grid container item spacing={4}>
              {sortedRecentlySearchedMarketsToShow &&
                sortedRecentlySearchedMarketsToShow.map((market, index) => (
                  <Grid item xs={12} sm={6} lg={4} xl={3} key={index}>
                    <MarketCard
                      district={market.district}
                      country={market.country}
                      isSubscribed={market.isSubscribed}
                      city={market.city}
                      isDemo={market.isDemo}
                      index={index}
                      marketType={tab}
                      id={market.id}
                      setSavedMarkets={setSavedMarkets}
                      user={user}
                      searchMarketOrigin={market.origin}
                      recentlyVisitedMarketsFromLocalStorage={
                        recentlyVisitedMarketsFromLocalStorage
                      }
                      setRecentlySearchedMarkets={setRecentlySearchedMarkets}
                      recentlySearchedMarkets={recentlySearchedMarkets}
                    />
                  </Grid>
                ))}
            </Grid>
          )}

          {tab == 'saved' && !showAdvancedDashboard && (
            <Grid container item spacing={4}>
              {sortedMarketsToShow.length > 0 ? (
                sortedMarketsToShow.map((market, index) => (
                  <Grid item xs={12} sm={6} lg={4} xl={3} key={index}>
                    {market.isSubmarket ? (
                      <SubmarketCard
                        district={market.district}
                        parentName={market.parent_market_name}
                        id={market.id}
                        index={index}
                        savedSubmarkets={savedSubmarkets}
                        setSavedSubmarkets={setSavedSubmarkets}
                        userSubscriptionPlan={user.userSubscriptionPlan}
                      />
                    ) : (
                      <MarketCard
                        district={market.district}
                        country={market.country}
                        isSubscribed={market.isSubscribed}
                        city={market.city}
                        isDemo={market.isDemo}
                        index={index}
                        marketType={tab}
                        id={market.id}
                        setSavedMarkets={setSavedMarkets}
                        user={user}
                        searchMarketOrigin={market.origin}
                        recentlyVisitedMarketsFromLocalStorage={
                          recentlyVisitedMarketsFromLocalStorage
                        }
                        setRecentlySearchedMarkets={setRecentlySearchedMarkets}
                        recentlySearchedMarkets={recentlySearchedMarkets}
                      />
                    )}
                  </Grid>
                ))
              ) : (
                <Grid item xs={12} sx={{ width: '100%' }}>
                  <Alert severity="info">
                    You haven’t saved a market yet. Save a market to view its summary here or
                    compare them by switching to table view.{' '}
                    <a
                      href="https://youtu.be/JGJFRhfbmu4"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: COLOR_PRIMARY_2, fontWeight: 500 }}
                    >
                      Learn how to save a market
                    </a>{' '}
                    and get started!
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default OverviewLandingPage;
